import {
  AddGoogleWorkspaceProjectRequestConfig,
  CreateGoogleWorkspaceConfigRequestConfig,
  DeleteGoogleWorkspaceConfigRequestConfig,
  type GoogleCalendarDefault,
  type GoogleCalendarPreference,
  GoogleCalendarPreferenceRequestConfig,
  type GoogleDeployment,
  type GoogleDiscoverResponse,
  type GoogleFolderCount,
  type GoogleSyncMode,
  type GoogleVerifyScopesResponse,
  type GoogleWorkspaceConfig,
  GoogleWorkspaceConfigsRequestConfig,
  GoogleWorkspaceDeploymentRequestConfig,
  GoogleWorkspaceDiscoverRequestConfig,
  GoogleWorkspaceFolderCountRequestConfig,
  GoogleWorkspaceScopesRequestConfig,
  RemoveGoogleWorkspaceProjectRequestConfig,
  SyncGoogleWorkspaceDeploymentRequestConfig,
  UpdateGoogleCalendarDefaultRequestConfig,
  type UpdateGoogleCalendarPreferenceRequest,
  UpdateGoogleCalendarPreferenceRequestConfig,
  UpdateGoogleWorkspaceSubjectRequestConfig,
  UpdateGoogleWorkspaceSyncModeRequestConfig,
  useStorytellClient,
  VerifyGoogleWorkspaceScopesRequestConfig,
} from "@storytell/client";
import { err, ok, type Result } from "neverthrow";
import { createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import { type FeatureAction, trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

export type {
  GoogleDeployment,
  GoogleDiscoverNode,
  GoogleScopeCheck,
  GoogleSyncMode,
  GoogleVerifyScopesResponse,
  GoogleWorkspaceConfig,
} from "@storytell/client";

interface CreateGoogleWorkspaceInput {
  projectIds: string[];
  googleDomain: string;
  saKeyJson: string;
}

// Wraps the server error envelope as an Error, preserving `code` for translation.
class GoogleWorkspaceError extends Error {
  readonly errorResult: { code?: string | number; message?: string; statusCode?: number };

  constructor(payload: { code?: string | number; message?: string; statusCode?: number }) {
    const message = payload.message ?? "Request failed";
    super(message);
    this.name = "GoogleWorkspaceError";
    this.errorResult = {
      code: payload.code,
      message,
      statusCode: payload.statusCode,
    };
  }
}

const toError = (e: Error): GoogleWorkspaceError => {
  const meta = e as Error & { code?: string; status?: number };
  return new GoogleWorkspaceError({
    code: meta.code ?? meta.status,
    message: e.message,
    statusCode: meta.status,
  });
};

export function useGoogleWorkspace() {
  const wire = useWire();
  const client = useStorytellClient();

  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<GoogleWorkspaceError | null>(null);

  // Shared envelope for write requests: centralizes the saving flag and routes
  // failures through setError so each caller only supplies the fetch.
  const mutate = async <T>(
    doFetch: () => Promise<Result<T, Error>>,
    action: FeatureAction<"integration.google">
  ): Promise<Result<T, GoogleWorkspaceError>> => {
    if (isServer) return err(new GoogleWorkspaceError({ statusCode: 0 }));
    setError(null);
    setIsSaving(true);
    try {
      const res = await doFetch();
      if (res.isErr()) {
        const e = toError(res.error);
        setError(e);
        return err(e);
      }
      trackFeature({ feature: "integration.google", action, via: "button" });
      return ok(res.value);
    } finally {
      setIsSaving(false);
    }
  };

  const listConfigs = async (): Promise<Result<GoogleWorkspaceConfig[], GoogleWorkspaceError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      GoogleWorkspaceConfigsRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined,
      {
        orgIdOverride: wire.currentOrgId(),
      }
    );
    if (res.isErr()) {
      // Grants maps out-of-scope reads to 404, so non-admins get an empty list
      // instead of an error.
      if ((res.error as Error & { status?: number }).status === 404) return ok([]);
      return err(toError(res.error));
    }
    return ok(res.value);
  };

  const createConfig = async (
    input: CreateGoogleWorkspaceInput
  ): Promise<Result<GoogleWorkspaceConfig, GoogleWorkspaceError>> => {
    let parsedKey: unknown;
    try {
      parsedKey = JSON.parse(input.saKeyJson);
    } catch {
      parsedKey = null;
    }
    if (typeof parsedKey !== "object" || parsedKey === null || Array.isArray(parsedKey)) {
      // No statusCode: extractErrorInfo would let it shadow the dotted code,
      // which is the one that translates.
      const invalid = new GoogleWorkspaceError({ code: "google_integration.client.invalid_key_json" });
      setError(invalid);
      return err(invalid);
    }
    const saKeyJson = parsedKey as Record<string, unknown>;

    return mutate(
      () =>
        client.fetch(
          CreateGoogleWorkspaceConfigRequestConfig.withBearerToken(wire.auth.token()),
          undefined,
          { projectIds: input.projectIds, googleDomain: input.googleDomain, saKeyJson },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "connect"
    );
  };

  const deleteConfig = (integrationId: string): Promise<Result<void, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          DeleteGoogleWorkspaceConfigRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          undefined,
          {
            orgIdOverride: wire.currentOrgId(),
          }
        ),
      "disconnect"
    );

  const addProject = (
    integrationId: string,
    projectId: string
  ): Promise<Result<GoogleWorkspaceConfig, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          AddGoogleWorkspaceProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { projectId },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "deploy"
    );

  const removeProject = (integrationId: string, projectId: string): Promise<Result<void, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          RemoveGoogleWorkspaceProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "undeploy"
    );

  // An empty subjectEmail clears the subject and turns Shared-Drive browsing
  // off. A non-empty one is validated against Google before it is stored.
  const updateSubject = (
    integrationId: string,
    subjectEmail: string
  ): Promise<Result<GoogleWorkspaceConfig, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          UpdateGoogleWorkspaceSubjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { subjectEmail },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  // allowPrune must be true for a narrowing change; the backend rejects an
  // unconfirmed narrowing with 409.
  const updateSyncMode = (
    integrationId: string,
    projectId: string,
    input: { syncMode: GoogleSyncMode; selectedRootIds: string[]; allowPrune: boolean }
  ): Promise<Result<void, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          UpdateGoogleWorkspaceSyncModeRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          input,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  // The queued sync always prunes, so it deletes assets for files that have left
  // the deployment's scope. 409 when one is already queued or running.
  const syncNow = (integrationId: string, projectId: string): Promise<Result<void, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          SyncGoogleWorkspaceDeploymentRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "sync_now"
    );

  const updateCalendarPreference = (
    orgId: string,
    input: UpdateGoogleCalendarPreferenceRequest
  ): Promise<Result<GoogleCalendarPreference, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(UpdateGoogleCalendarPreferenceRequestConfig.withBearerToken(wire.auth.token()), undefined, input, {
          orgIdOverride: orgId,
        }),
      "calendar_preference"
    );

  // Only pre-selects the toggle for members who have not answered; it never
  // enables scheduling on their behalf.
  const updateCalendarDefault = (
    integrationId: string,
    defaultEnabled: boolean
  ): Promise<Result<GoogleCalendarDefault, GoogleWorkspaceError>> =>
    mutate(
      () =>
        client.fetch(
          UpdateGoogleCalendarDefaultRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { defaultEnabled },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "calendar_default"
    );

  // The reads below don't go through mutate(), so they leave isSaving and error
  // alone and callers own their own loading state.
  const discover = async (
    integrationId: string,
    opts?: { parentId?: string; driveId?: string; resolveIds?: string[] }
  ): Promise<Result<GoogleDiscoverResponse, GoogleWorkspaceError>> => {
    if (isServer) return ok({ nodes: [], resolved: [], hasMore: false });
    const res = await client.fetch(
      GoogleWorkspaceDiscoverRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId, parentId: opts?.parentId, driveId: opts?.driveId, resolveIds: opts?.resolveIds },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  const getDeployment = async (
    integrationId: string,
    projectId: string
  ): Promise<Result<GoogleDeployment, GoogleWorkspaceError>> => {
    const res = await client.fetch(
      GoogleWorkspaceDeploymentRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId, projectId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  // Drive has no cheap folder total, so this counts a single page: at most 100,
  // with hasMore set when the folder holds more.
  const folderCount = async (
    integrationId: string,
    folderId: string,
    driveId?: string
  ): Promise<Result<GoogleFolderCount, GoogleWorkspaceError>> => {
    if (isServer) return ok({ count: 0, hasMore: false });
    const res = await client.fetch(
      GoogleWorkspaceFolderCountRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId, folderId, driveId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  // 404 when the org has no Google integration, which callers surface as "no
  // calendar opt-in to offer" rather than a failure.
  const getCalendarPreference = async (
    orgId: string
  ): Promise<Result<GoogleCalendarPreference | null, GoogleWorkspaceError>> => {
    if (isServer) return ok(null);
    const res = await client.fetch(
      GoogleCalendarPreferenceRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined,
      { orgIdOverride: orgId }
    );
    if (res.isErr()) {
      if ((res.error as Error & { status?: number }).status === 404) return ok(null);
      return err(toError(res.error));
    }
    return ok(res.value);
  };

  // Not owner-gated, so a plain member gets the scope list too.
  const listScopes = async (): Promise<Result<string[], GoogleWorkspaceError>> => {
    if (isServer) return ok([]);
    const res = await client.fetch(
      GoogleWorkspaceScopesRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value.scopes);
  };

  // Clears the recorded scope gap server-side when every scope comes back
  // granted. Takes a few seconds; nothing waits on it.
  const verifyScopes = async (
    integrationId: string
  ): Promise<Result<GoogleVerifyScopesResponse, GoogleWorkspaceError>> => {
    if (isServer) return err(new GoogleWorkspaceError({ statusCode: 0 }));
    const res = await client.fetch(
      VerifyGoogleWorkspaceScopesRequestConfig.withBearerToken(wire.auth.token()),
      { integrationId },
      undefined,
      { orgIdOverride: wire.currentOrgId() }
    );
    if (res.isErr()) return err(toError(res.error));
    return ok(res.value);
  };

  return {
    listConfigs,
    createConfig,
    deleteConfig,
    addProject,
    removeProject,
    updateSubject,
    updateSyncMode,
    syncNow,
    getCalendarPreference,
    updateCalendarPreference,
    updateCalendarDefault,
    discover,
    getDeployment,
    folderCount,
    listScopes,
    verifyScopes,
    isSaving,
    error,
    clearError: () => setError(null),
  };
}
